import { canUseConsentPurposeOnClient } from '../../consent/waitingForConsent'
import { useEffect, useMemo } from 'react'

import { fetchHighIntentOffers, fetchPeopleLikeMeRecommendations, fetchPersonalisedPreferenceOffers } from 'actions/RecommendationActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { selectTwoTowerOfferListFiltersByUser } from 'selectors/recommendations'
import useOfferList from 'hooks/Offers/useOfferList'
import { get as getLocalStorage } from 'lib/storage/expirableLocalStorage'
import { unique, without } from 'lib/array/arrayUtils'

const listingOfferOptions = { preferListingOffers: true }

interface Props {
  excludedIds?: Array<string>
}

/**
 * Constructs a list of personalized offers for the user in this priority order:
 * 1. Two towers personalised offers if available
 * 2. "People like me" recommendations if user is logged in
 * 3. Travel preference based offers - shown first if preferences were updated in last 24h
 *
 * High intent offers are excluded since they are displayed separately.
 */
export default function useTPFMOfferList({ excludedIds }: Props): App.OfferList {
  const canPersonalise = canUseConsentPurposeOnClient('personalisation')
  const dispatch = useAppDispatch()
  const travelPreferencesUpdatedInLast24Hours = useMemo(() => getLocalStorage<number>('travelPreferencesLastUpdatedAt'), [])
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const peopleLikeMe = useAppSelector(state => state.recommendations.personalisedPeopleLikeMe)
  const personalisedPreferences = useAppSelector(state => state.recommendations.personalisedPreferences)
  const highIntent = useAppSelector(state => state.recommendations.highIntent)
  const twoTowerFilters = useAppSelector(selectTwoTowerOfferListFiltersByUser)
  const twoTowersOfferList = useOfferList(twoTowerFilters, listingOfferOptions)

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(fetchPeopleLikeMeRecommendations())
      dispatch(fetchPersonalisedPreferenceOffers())
    }

    dispatch(fetchHighIntentOffers())
  }, [dispatch, isLoggedIn])

  const offerList = useMemo<App.OfferList>(() => {
    if (!canPersonalise) return { fetching: false, error: undefined, offerIds: [], key: 'topPicks' }
    let candidates: Array<string> = []

    if (!twoTowersOfferList.fetching && twoTowersOfferList.offerIds.length > 0) {
      candidates = twoTowersOfferList.offerIds
    }
    else if (isLoggedIn && peopleLikeMe.state === 'done' && peopleLikeMe.offers.length > 0) {
      candidates = peopleLikeMe.offers.map(offer => offer.offerId)
    }

    const preferenceOffers = personalisedPreferences.offers.map(offer => offer.offerId)
    candidates = travelPreferencesUpdatedInLast24Hours ? [...preferenceOffers, ...candidates] : [...candidates, ...preferenceOffers]
    candidates = unique(candidates)

    const fetching = twoTowersOfferList.fetching || ['loading', 'initial'].includes(highIntent.state) || (isLoggedIn && [peopleLikeMe.state, personalisedPreferences.state].some(state => ['loading', 'initial'].includes(state)))
    const error = twoTowersOfferList.error || personalisedPreferences.error || peopleLikeMe.error
    // Exclude high intent offers as they are shown above the TPFM carousel on the homepage
    const offerIds = without(candidates, ...highIntent.offers.map(offer => offer.offerId), ...(excludedIds || []))

    return {
      fetching,
      error,
      offerIds,
      key: 'topPicks',
    }
  }, [canPersonalise, twoTowersOfferList, isLoggedIn, peopleLikeMe, highIntent, personalisedPreferences, travelPreferencesUpdatedInLast24Hours, excludedIds])

  return offerList
}
